import { FC, useCallback, useEffect, useMemo, useState } from "react";
import "fine-uploader";
import qq from "fine-uploader/lib/core/all.js";
import Button from "../Button/Button";
import FileRow from "./components/FileRow/FileRow";
import { clss } from "../../utils/helpers/styleHelpers";
import Icon from "../Icon/Icon";
import { P, Span } from "../Text/Text";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { ProjectStatusE, StatusE, UserRoleE } from "../../models/enums";
import { ProjectT, VersionT } from "../../models/models";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { useBeforeunload } from "react-beforeunload";
import { useDragUploader } from "../../utils/hooks/useDragUploader";
import { BASE_URL } from "../../api/config";
import usePopup from "../../utils/hooks/usePopup";
import { type FineUploader } from "fine-uploader";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { getLoggedUserToken } from "../../utils/helpers/tokenStorage";
import { useSetFileUploadedMutation } from "../../api/projectsApi";
import type { BatchFileT } from "./models/models";
import checkAddedFiles from "./Helpers/checkAddedFiles";

import styles from "./uploader.module.scss";
import { useSetS3UploadErrorMutation } from "../../api/versionAPI";
import { useGetUploadConfigQuery } from "../../api/uploadConfigAPI";

type FineUploaderComponentPropTypes = {
  versions: VersionT[];
  projectId: number;
  project: ProjectT;
  handleClose: () => void;
};

const PREVIEW_WIDTH = 320;
const PREVIEW_HEIGHT = 240;

const FineUploaderComponent: FC<FineUploaderComponentPropTypes> = (props) => {
  const [uploader, setUploader] = useState<FineUploader>(null);
  const { createPopup, removePopup } = usePopup();
  const { versions, projectId, project, handleClose } = props;
  const [totalProgress, setTotalProgress] = useState(0);
  const [thumbnailSrc, setThumbnailSrc] = useState<string>();
  const [thumbnailPosition, setThumbnailPosition] = useState([0, 0]);
  const [filesProgress, setFilesProgress] = useState<any>({});
  const [replacedNames, setReplacedNames] = useState({});
  const user = useAppSelector(selectCurrentUser);
  const { handleDrag, handleDrop } = useDragUploader();
  const { t } = useTranslate();
  const [setFileUploaded] = useSetFileUploadedMutation();
  const [setS3UploadError] = useSetS3UploadErrorMutation();
  const { data: uploadConfig } = useGetUploadConfigQuery();
  const [renderSwitcher, setRenderSwitcher] = useState(false);

  useEffect(() => {
    Object.keys(filesProgress).length &&
      setTotalProgress(
        +(
          Object.values(filesProgress).reduce(
            (acc: number, curr) => acc + +curr,
            0
          ) as number
        ).toFixed(2)
      );
  }, [filesProgress]);

  const files = useMemo(() => {
    return uploader
      ? (uploader.getUploads() as BatchFileT[]).filter(
        (upload) => upload.status !== "canceled"
      )
      : [];
  }, [uploader?.getUploads(), renderSwitcher]);

  const handleRemoveFile = (id: number) => {
    if (uploader) {
      uploader.cancel(id);
    }
  };

  useBeforeunload((e) => {
    const backButton = document.getElementById("wizard-back-button");

    if (!backButton) {
      e.preventDefault();
    }
  });

  const handleRemoveFileByName = useCallback(
    (e) => {
      const passedName = e.detail.name;

      if (uploader) {
        const allFiles = uploader.getUploads();

        const file = (allFiles as BatchFileT[]).find(
          (file) => file.name === passedName
        );

        if (file) {
          uploader.cancel(file.id);
        }
      }
    },
    [uploader]
  );

  const handleUploaderAddFile = useCallback(
    (e) => {
      const data = e.detail.file;
      const fileNameLastPart = data.name.split(".").pop();

      const validName = e.detail.fileName + "." + fileNameLastPart;
      const checkName = e.detail.checkName;

      const isFileAdded = checkAddedFiles(
        [data],
        createPopup,
        removePopup,
        uploader,
        versions,
        user,
        project,
        t,
        !checkName ? validName : undefined
      );

      isFileAdded && setReplacedNames((prev) => ({ ...prev, [e.detail.fileName]: data.name }));
    },
    [createPopup, removePopup, uploader, versions, user, project, t]
  );

  useEffect(() => {
    document.addEventListener("uppy-add-file", handleUploaderAddFile);
    document.addEventListener("uppy-remove-file", handleRemoveFileByName);
    return () => {
      document.removeEventListener("uppy-add-file", handleUploaderAddFile);
      document.removeEventListener("uppy-remove-file", handleRemoveFileByName);
    };
  }, [
    uploader,
    setReplacedNames,
    handleUploaderAddFile,
    handleRemoveFileByName,
  ]);

  useEffect(() => {
    // Bucket, region and key id come from the backend (api/UploadConfig -> BucketName / AwsRegion /
    // AwsAccessKeyId in JumboMedia.Web/Web.config + its Debug/Test/Release transforms). The browser
    // PUTs straight to S3 and SignAwsUrlForFineUploader only HMAC-signs the policy without inspecting
    // it, so this is the real upload destination. It used to be hardcoded here with the bucket picked
    // by sniffing window.location.origin — the deployed hostname decided the destination instead of
    // config, and any origin the table did not name fell through to the PRODUCTION bucket. Nothing is
    // inferred from the hostname any more.
    if (
      !uploadConfig?.BucketName ||
      !uploadConfig?.Region ||
      !uploadConfig?.AccessKeyId
    ) {
      // No fallback on purpose: an unconfigured environment must fail visibly (the "Uploader is not
      // available" branch below) rather than quietly upload to whichever bucket was compiled in.
      return;
    }

    const endpoint = `https://${uploadConfig.BucketName}.s3-${uploadConfig.Region}.amazonaws.com`;
    const accessKey = uploadConfig.AccessKeyId;

    const uploader = new qq.s3.FineUploaderBasic({
        autoUpload: false,

        request: {
          endpoint,
          accessKey,
        },

        objectProperties: {
          key: function (fileId) {
            return `media/${projectId}/${this.getName(fileId) || "defaultFileName.mp4"}`;
          },
        },

        signature: {
          endpoint: BASE_URL + "api/Projects/SignAwsUrlForFineUploader",
          customHeaders: {
            Authorization: `Bearer ${getLoggedUserToken()}`,
          },
        },

        cors: {
          expected: true,
        },

        retry: {
          enableAuto: false,
          autoAttemptDelay: 1,
          maxAutoAttempts: 100,
        },

        resume: {
          enabled: false,
        },

        validation: {
          stopOnFirstInvalidFile: false,
        },

        workarounds: {
          iosEmptyVideos: false,
        },

        debug: false,

        multiple: true,

        chunking: {
          enabled: true,
          concurrent: {
            enabled: true,
          },
          partSize: 26843550,
        },
        callbacks: {
          onSubmit: (id, fileName) => {
            // uploader?.setUuid(
            //   id,
            //   `media/${projectId}/${versions.find(
            //     (version) => fileName.split(".")[0] === version.AdId
            //   )?.AdId
            //   }`
            // );
            setRenderSwitcher((prev) => !prev);
          },
          onUpload: async (id, fileName) => {
            return await setFileUploaded({
              sessionID: projectId,
              fileName: fileName,
              filePath: projectId + "/" + fileName,
              fileSize: uploader.getFile(id)?.size,
              uploaded: true,
              replaceMXF:
                project?.StatusName === StatusE.DownloadedByBroadcaster ||
                project?.StatusName === StatusE.DownloadedByAllBroadcasters,
            });
          },
          onProgress: (id, fileName, uploadedSize = 0) => {
            const fileSize = uploader.getFile(id)?.size;
            const uploadedSizeInPercents = uploadedSize / (fileSize / 100);
            setFilesProgress((prev) => ({
              ...prev,
              [id]: uploadedSizeInPercents.toFixed(2),
            }));
          },
          onComplete: (id, fileName) => {
            setTimeout(() => {
              handleClose();
            }, 3000);
          },
          onAllComplete: (id, fileName) => { },
          onCancel: (id, fileName) => {
            setRenderSwitcher((prev) => !prev);

            const removeFileCustomEvent = new CustomEvent("file-removed", {
              detail: {
                adId: fileName.split(".")[0],
              },
            });

            document.dispatchEvent(removeFileCustomEvent);
          },
          onError: (id, fileName) => { },
          onUploadError: (id, fileName) => {
            setS3UploadError({
              projectId,
              filename: fileName,
            });
          },
        },
      });
    setUploader(uploader);
  }, [uploadConfig]);

  const handleShowThumbnail = (e: any, file: any) => {
    setThumbnailSrc(URL.createObjectURL(file));
  };

  const handleMouseMove = (e: any) => {
    const maxX = document.body.clientWidth / 2 + PREVIEW_WIDTH;

    if (e.pageX > maxX) {
      setThumbnailSrc("");
    } else {
      setThumbnailPosition([e.pageX, e.screenY - e.pageY + 280]);
    }
  };

  const handleAddFiles = (e) => {
    const files: File[] = Array.from(e.target.files);


    checkAddedFiles(
      files,
      createPopup,
      removePopup,
      uploader,
      versions,
      user,
      project,
      t
    );

    e.target.value = "";
  };

  const totalSize = useMemo(
    () =>
      files?.length
        ? +(
          (files as BatchFileT[])?.reduce(
            (acc: number, curr) => (acc += curr.size),
            0
          ) /
          1024 /
          1024
        ).toFixed(2)
        : 0,
    [files]
  );

  const handleStartUpload = () => {
    uploader?.uploadStoredFiles();
  };

  const handleAddFilesClick = () => {
    document.getElementById("file-input").click();
  };

  const closeUploader = () => {
    totalProgress === 0 && handleClose();
    uploader.reset();
  };

  if (!uploader)
    return (
      <div className={styles.note}>
        <P>Uploader is not available</P>
      </div>
    );

  return (
    <>
      <div className={styles.note}>
        {!isMobileOrTablet() && (
          <P>
            To select the files to upload, drag the video file required for each
            version or click the upload icon of the relevant version record.
          </P>
        )}
        <P>
          After selecting the files, click the upload button to start uploading.
        </P>
      </div>
      <div
        onDragOver={(e) => handleDrag(e, "uppy-root")}
        onDragStart={(e) => handleDrag(e, "uppy-root")}
        onDragLeave={(e) => handleDrag(e, "uppy-root")}
        onDrop={(e) => handleDrop(e, "", true, true)}
        id="uppy-root"
      >
        <input
          id="file-input"
          type="file"
          multiple
          accept=".mp4, .mxf"
          className={styles.hiddenInput}
          onChange={handleAddFiles}
        />
        <div className={styles.uploaderWrapper}>
          <div className={styles.uploaderHeader}>
            <p className={styles.title}>
              <Span>Upload files</Span>
              {(files as BatchFileT[]).length
                ? ` (${(files as BatchFileT[]).length}/${versions.length || 0})`
                : ""}
            </p>
            <P className={styles.hint}>
              * File names must match the AD-ID of the versions shown in the
              version table
            </P>
            <Icon
              name="selectFormArrow"
              className={styles.closeIcon}
              onClick={closeUploader}
            />
          </div>
          <div className={clss([styles.dropZone, "uppy-dropzone"])}>
            {!!thumbnailSrc && (
              <div
                className={styles.previewWrapper}
                style={{
                  top: thumbnailPosition[1],
                  insetInlineStart: thumbnailPosition[0] - PREVIEW_WIDTH,
                }}
              >
                <video
                  width={PREVIEW_WIDTH}
                  height={PREVIEW_HEIGHT}
                  controls={false}
                  autoPlay
                  muted
                >
                  <source src={thumbnailSrc} type="video/mp4" />
                </video>
              </div>
            )}
            {(files as BatchFileT[])?.map((upload) => (
              <div
                onMouseMove={handleMouseMove}
                onMouseEnter={(e: any) => handleShowThumbnail(e, upload.file)}
                onMouseLeave={() => setThumbnailSrc("")}
                key={upload.id + ""}
              >
                <FileRow
                  batchFile={upload}
                  onRemove={handleRemoveFile}
                  progress={+filesProgress?.[upload.id] || 0}
                  handleShowThumbnail={handleShowThumbnail}
                  replacedNames={replacedNames}
                />
              </div>
            ))}
          </div>
          <div
            className={styles.progressBar}
            style={{
              width: `${totalProgress}%`,
              opacity:
                project.Status === ProjectStatusE.Opened ||
                  project.Status === ProjectStatusE.UploadingFiles
                  ? 1
                  : 0,
            }}
          />
          <div className={styles.uploaderFooter}>
            {!totalProgress && user?.role !== UserRoleE.Broadcaster && (
              <Button type="file" onClick={handleAddFilesClick}>
                Add files
              </Button>
            )}
            {user?.role === UserRoleE.Broadcaster && <span />}
            <div className={styles.uploaderFooterData}>
              <span className={styles.info}>
                {(
                  (totalSize / 100) *
                  (totalProgress /
                    (uploader.getUploads() as BatchFileT[])?.length || 0)
                ).toFixed(2)}{" "}
                MB / {totalSize} MB
              </span>
              <span className={clss([styles.info, styles.percentages])}>
                {(
                  totalProgress /
                  (uploader.getUploads() as BatchFileT[])?.length || 0
                ).toFixed(0)}
                %
              </span>
              {!totalProgress && (
                <Button
                  disabled={
                    !(files as BatchFileT[]).length || totalProgress > 0
                  }
                  onClick={handleStartUpload}
                  loading={totalProgress > 0}
                >
                  Start upload
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FineUploaderComponent;
